import { random } from 'lodash';

import { TEnemy, generateBaronBoss, generateBeastBoss, generateRandomBandit, generateRandomGobbo, generateUgonBoss } from '../enemy/enemy.generators';
import { ImmerStateCreator } from './store.types';

type TEnemyState = {
   state: {
      enemies: Record<string, TEnemy>;
   };
};

type TEnemyActions = {
   actions: {
      attackEnemy(params: { enemyId: string; amount: number }): void;
      bashEnemy(enemyId: string): void;
      setEnemies(params: { enemies: TEnemy[] }): void;
      generateRandomGobbos(count: number): void;
      generateRandomBandits(count: number): void;
      setActiveTarget(enemyId: string): void;
      generateBaronBoss(): void;
      generateBeastBoss(): void;
      generateUgonBoss(): void;
      incrementRoundsStunned(enemyId: string): void;
      recoverFromStun(enemyId: string): void;
   };
};

type TEnemyGetters = {
   getters: {
      getEnemiesThatAreReadyToFight(): TEnemy[];
      getEnemiesThatAreStunned(): TEnemy[];
      getActiveTargetEnemy(): string;
   };
};

type TEnemySlice = TEnemyState & TEnemyActions & TEnemyGetters;
export type TNamedEnemySlice = { enemy: TEnemySlice };

export const getEnemyInitialState = (): TEnemyState['state'] => {
   return {
      enemies: {},
   }
};

export const createEnemySlice: ImmerStateCreator<TNamedEnemySlice, TEnemySlice> = (set, get, store) => ({
   /***************************
    * State
    ***************************/

   // the active enemies in our state is an object with enemy IDs as keys
   // storing as an object allows us to easily select an enemy without having to iterate through an array to find the one we want every time

   state: getEnemyInitialState(),

   /***************************
    * Actions
    ***************************/
   actions: {
      /**
       * Attacks a specific enemy with a specified amount of damage.
       *
       * @param {Object} param - The destructured parameter object.
       * @param {string} param.enemyId - The ID of the enemy to attack.
       * @param {number} param.amount - The amount of damage to apply to the enemy.
       * @returns The result of the set action, applying the specified amount of damage to the specified enemy.
       */
      attackEnemy: ({ enemyId, amount }) => {
         return set((state) => {
            // find the enemy from the state
            const enemy = state.enemy.state.enemies[enemyId];
            let newHealth = enemy.currentHealth - amount;

            // don't allow negative health
            if (newHealth < 0) {
               newHealth = 0;
            }

            // set the enemy's new health
            enemy.currentHealth = newHealth;

            // if the enemy's health is 0, they are dead
            if (newHealth === 0) {
               enemy.currentHealth = 0;
               enemy.isDead = true;
               enemy.isStunned = false;
               enemy.isActiveTarget = false;

               //find new default target
               for (const enemyKey in state.enemy.state.enemies) {
                  const anotherEnemy = state.enemy.state.enemies[enemyKey];
                  if (!anotherEnemy.isDead) {
                     anotherEnemy.isActiveTarget = true;
                     break;
                  }
               }
            }
         });
      },

      bashEnemy(enemyId: string) {
         return set((state) => {
            // find the enemy from the state
            const enemy = state.enemy.state.enemies[enemyId];

            //set them to be stunned only if they fail a resistance check.
            const diceRoll = random(0, 100);
            if (diceRoll > enemy.resistance) {
               enemy.isStunned = true;
            }
         });
      },

      /**
       * Sets the enemies in the store.
       *
       * @param {Array} params.enemies - The array of enemies to set.
       * @returns The result of the set action, setting the enemies in the store.
       */
      setEnemies: ({ enemies }) => {
         return set((state) => {
            state.enemy.state.enemies = enemies.reduce((acc, enemy) => {
               acc[enemy.id] = enemy;
               return acc;
            }, {} as Record<string, TEnemy>);
         });
      },

      /**
       * Generates a random number of enemies.
       *
       * @param {Object} params - The destructured parameter object.
       * @param {number} params.count - The number of enemies to generate.
       * @returns The result of the setEnemies action, setting the generated enemies in the store.
       */
      generateRandomGobbos: (count: number) => {
         const newEnemies = Array.from({ length: count || random(1, 5) }, () => generateRandomGobbo());
         newEnemies[0].isActiveTarget = true;
         return get().enemy.actions.setEnemies({ enemies: newEnemies });
      },

      generateRandomBandits: (count: number) => {
         const newEnemies = Array.from({ length: count || random(1, 5) }, () => generateRandomBandit());
         newEnemies[0].isActiveTarget = true;
         return get().enemy.actions.setEnemies({ enemies: newEnemies });
      },

      generateBeastBoss: () => {
         const newEnemies = Array.from({ length: 1 }, () => generateBeastBoss());
         newEnemies[0].isActiveTarget = true;
         return get().enemy.actions.setEnemies({ enemies: newEnemies });
      },

      generateBaronBoss: () => {
         const newEnemies = Array.from({ length: 1 }, () => generateBaronBoss());
         newEnemies[0].isActiveTarget = true;
         return get().enemy.actions.setEnemies({ enemies: newEnemies });
      },

      generateUgonBoss: () => {
         const newEnemies = Array.from({ length: 1 }, () => generateUgonBoss());
         newEnemies[0].isActiveTarget = true;
         return get().enemy.actions.setEnemies({ enemies: newEnemies });
      },

      setActiveTarget: (enemyId: string) => {
         return set((state) => {
            for (const enemyKey in state.enemy.state.enemies) {
               const enemy = state.enemy.state.enemies[enemyKey];
               enemy.isActiveTarget = false;
            }
            const targetEnemy = state.enemy.state.enemies[enemyId];
            targetEnemy.isActiveTarget = true;
         });
      },

      incrementRoundsStunned(enemyId: string) {
         return set((state) => {
            const targetEnemy = state.enemy.state.enemies[enemyId];
            targetEnemy.roundsStunned++;
         });
      },

      recoverFromStun(enemyId: string) {
         return set((state) => {
            const targetEnemy = state.enemy.state.enemies[enemyId];
            targetEnemy.isStunned = false;
            targetEnemy.resistance = 95; //make it very unlikely to be stunned again
         });
      },

   },

   /***************************
    * Getters
    ***************************/
   getters: {
      getEnemiesThatAreReadyToFight: (): TEnemy[] => {
         return Object.values(get().enemy.state.enemies).filter((enemy) => !enemy.isDead && !enemy.isStunned);
      },

      getEnemiesThatAreStunned: (): TEnemy[] => {
         return Object.values(get().enemy.state.enemies).filter((enemy) => enemy.isStunned);
      },

      getActiveTargetEnemy() {
         const targetEnemy = Object.values(get().enemy.state.enemies).filter((enemy) => enemy.isActiveTarget);
         return targetEnemy[0].id;
      },
   },
});
